import {ExpertSystemEquipmentType, HistoricalDialogData} from '@domain/expert-system'
import {KpiCalculationError} from '@hconnect/common/types'
import {CollapsableRow, PinnableHeaderTableCell, useIsMobile} from '@hconnect/uikit'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material'
import {isNil} from 'lodash'
import React, {FC, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  DAY_KEYS,
  ESDR_TABLE_WIDTHS,
  ESUR_TABLE_LEFTS,
  ESUR_TABLE_WIDTHS,
  FEATURE_NAMESPACE,
  MONTH_MAP,
  STICKY_COLUMNS_CONFIG
} from '../consts'
import {
  useExpandCollapse,
  useRenderAverageCell,
  useTableCellStyle,
  useTableScrollState
} from '../hooks'
import {ExpertSystemUsageRateColumnKeys, HierarchyTableNode, MonthData} from '../types'
import {getDaysDetails, isKilnOrMillLevel, tableCellDataTestIdGenerator} from '../utils'

import {CommentKpiDialog} from './CommentKpiDialog'
import {LocationCellContent} from './LocationCellContent'
import {MonthCell} from './MonthCell'

const DAILY_STICKY_RIGHT: typeof STICKY_COLUMNS_CONFIG.right = {
  [ExpertSystemUsageRateColumnKeys.AVERAGE]: {
    width: ESUR_TABLE_WIDTHS.AVERAGE,
    right: 0
  }
}

type ExpertSystemDailyTableCellConfig = {
  isEditMode: boolean
  onMonthValueChange: (itemId: string, day: string, value?: number | KpiCalculationError) => void
  setNodeMonth: (data: HistoricalDialogData) => void
  setCommentPopup: (popupData: {
    item: HierarchyTableNode
    month: string
    day: number
    dayValue?: number
    dayKpiCalculationError?: KpiCalculationError
  }) => void
  date: Date
}

const useRenderExpertSystemDailyTableCell = ({
  isEditMode,
  onMonthValueChange,
  setNodeMonth,
  setCommentPopup,
  date
}: ExpertSystemDailyTableCellConfig) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const year = date.getFullYear()

  const renderAverageCell = useRenderAverageCell()

  const renderMillTypeCell = (item: HierarchyTableNode) => (
    <>{item.millType && t(`label.${item.millType}`)}</>
  )

  const renderDayCell = (item: HierarchyTableNode, key: string, dayData: MonthData) => {
    if (!dayData) return <span>-</span>
    const monthNum = String(date.getMonth() + 1).padStart(2, '0')
    const monthKey = MONTH_MAP[monthNum]
    return (
      <MonthCell
        hierarchyId={item.id}
        month={key}
        year={year}
        monthData={dayData}
        itemType={item.type}
        isEditMode={isEditMode && isKilnOrMillLevel(item.type)}
        onMonthValueChange={onMonthValueChange}
        canEdit={item.access.canWriteNode}
        setNodeMonth={(id) => setNodeMonth({hierarchyId: id, month: key, equipmentName: item.name})}
        onCommentClick={
          isKilnOrMillLevel(item.type) &&
          !isEditMode &&
          (!isNil(dayData?.value) || !isNil(dayData?.kpiCalculationError))
            ? () =>
                setCommentPopup({
                  item,
                  month: monthKey,
                  day: Number.parseInt(key, 10),
                  dayValue: dayData?.value,
                  dayKpiCalculationError: dayData?.kpiCalculationError
                })
            : undefined
        }
      />
    )
  }

  return (item: HierarchyTableNode, _columnIndex: number, columnKey: string): React.ReactNode => {
    const dataTestId = tableCellDataTestIdGenerator(item.id, columnKey)
    const key = columnKey as ExpertSystemUsageRateColumnKeys
    if (key === ExpertSystemUsageRateColumnKeys.LOCATION) {
      return (
        <LocationCellContent
          name={item.name}
          countryCode={item.countryCode}
          dataTestId={dataTestId}
        />
      )
    }
    if (key === ExpertSystemUsageRateColumnKeys.MILL_TYPE) {
      return renderMillTypeCell(item)
    }
    if (key === ExpertSystemUsageRateColumnKeys.AVERAGE) {
      return renderAverageCell(item)
    }
    if (DAY_KEYS.includes(columnKey)) {
      const dayData = item[columnKey as keyof HierarchyTableNode] as MonthData
      return renderDayCell(item, columnKey, dayData)
    }
    return null
  }
}

type ExpertSystemDailyTableProps = {
  data: HierarchyTableNode[]
  getIsExpanded: (id: string) => boolean
  toggleExpandCollapse: (id: string) => void
  showMillColumns: boolean
  date: Date
  isEditMode: boolean
  equipmentType: ExpertSystemEquipmentType
  onMonthValueChange: (itemId: string, day: string, value?: number | KpiCalculationError) => void
  setNodeMonth: (data: HistoricalDialogData) => void
}

export const ExpertSystemDailyTable: FC<ExpertSystemDailyTableProps> = ({
  data,
  toggleExpandCollapse,
  getIsExpanded,
  showMillColumns,
  date,
  isEditMode,
  equipmentType,
  onMonthValueChange,
  setNodeMonth
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)

  const {scrollingRight, scrollingLeft, handleScroll} = useTableScrollState()
  const {isItemExpanded, onItemToggled} = useExpandCollapse(getIsExpanded, toggleExpandCollapse)

  const getCellStyle = useTableCellStyle({
    stickyLeft: STICKY_COLUMNS_CONFIG.left,
    stickyRight: DAILY_STICKY_RIGHT,
    defaultWidth: ESDR_TABLE_WIDTHS.DAY,
    scrollingRight,
    scrollingLeft,
    backgroundColor: theme.palette.background.paper,
    hoverBackgroundColor: theme.palette.action.hover
  })

  const [commentPopup, setCommentPopup] = useState<{
    item: HierarchyTableNode
    month: string
    day: number
    dayValue?: number
    dayKpiCalculationError?: KpiCalculationError
  } | null>(null)

  const renderCell = useRenderExpertSystemDailyTableCell({
    isEditMode,
    onMonthValueChange,
    setNodeMonth,
    setCommentPopup,
    date
  })

  const language = i18n.language
  const dayNames = useMemo(() => getDaysDetails(date, language), [date, language])
  const activeDayKeys = useMemo(() => DAY_KEYS.slice(0, dayNames.length), [dayNames.length])

  const columnKeys: string[] = useMemo(
    () => [
      ExpertSystemUsageRateColumnKeys.LOCATION,
      ...(showMillColumns
        ? [
            ExpertSystemUsageRateColumnKeys.MILL_TYPE,
            ExpertSystemUsageRateColumnKeys.EXPERT_SYSTEM_NAME
          ]
        : []),
      ...activeDayKeys,
      ExpertSystemUsageRateColumnKeys.AVERAGE
    ],
    [showMillColumns, activeDayKeys]
  )

  return (
    <>
      <TableContainer
        onScroll={handleScroll}
        sx={{overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)'}}
      >
        <Table stickyHeader sx={{tableLayout: 'fixed'}}>
          <TableHead>
            <TableRow>
              <PinnableHeaderTableCell
                left={ESUR_TABLE_LEFTS.LOCATION}
                width={ESUR_TABLE_WIDTHS.LOCATION}
                showShadow={isMobile && scrollingRight}
                isSticky
              >
                {t('label.location')}
              </PinnableHeaderTableCell>
              {showMillColumns && (
                <>
                  <PinnableHeaderTableCell
                    left={ESUR_TABLE_LEFTS.MILL_TYPE}
                    width={ESUR_TABLE_WIDTHS.MILL_TYPE}
                    showShadow={isMobile && scrollingRight}
                    isSticky
                  >
                    {t('label.type')}
                  </PinnableHeaderTableCell>
                  <PinnableHeaderTableCell
                    left={ESUR_TABLE_LEFTS.EXPERT_SYSTEM_NAME}
                    width={ESUR_TABLE_WIDTHS.EXPERT_SYSTEM_NAME}
                    showShadow={isMobile && scrollingRight}
                    isSticky
                  >
                    {t('label.expertSystemName')}
                  </PinnableHeaderTableCell>
                </>
              )}
              {dayNames.map((dayName) => (
                <TableCell
                  sx={{width: `${ESDR_TABLE_WIDTHS.DAY}px`}}
                  key={`tableHeader-${dayName}`}
                >
                  {dayName}
                </TableCell>
              ))}
              <PinnableHeaderTableCell
                right={0}
                width={ESUR_TABLE_WIDTHS.AVERAGE}
                isSticky
                showShadow={isMobile && scrollingLeft}
              >
                {t('label.average')}
              </PinnableHeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <CollapsableRow
                key={item.id}
                item={item}
                isItemExpanded={isItemExpanded}
                onExpandToggleClicked={onItemToggled}
                columnKeys={columnKeys}
                renderCell={renderCell}
                getCellStyle={getCellStyle}
                expandCollapseOnlyOnFirstColumn
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {commentPopup && (
        <CommentKpiDialog
          item={commentPopup.item}
          progress={commentPopup.dayValue}
          kpiCalculationError={commentPopup.dayKpiCalculationError}
          kpiTargetEquipmentType={equipmentType}
          hierarchyId={commentPopup.item.id}
          month={commentPopup.month}
          year={date.getFullYear()}
          selectedDay={commentPopup.day}
          onClose={() => setCommentPopup(null)}
        />
      )}
    </>
  )
}
